import { apiClient } from '@/utils/api';

// Match status
export type MatchStatus = 'pending' | 'approved' | 'disputed' | 'cancelled';

/** What the disputer wants as the outcome */
export type DisputeDesiredOutcome = 'new_score' | 'invalidate';

// Score set (e.g., "21-18")
export interface ScoreSet {
  player1Score: number;
  player2Score: number;
}

// Player info in match response
export interface MatchPlayer {
  userId: string;
  displayName: string;
  avatar?: string;
}

// Match summary for list views
export interface MatchSummary {
  matchId: string;
  player1: MatchPlayer;
  player2: MatchPlayer;
  sets: ScoreSet[];
  winnerId: string;
  status: MatchStatus;
  submittedAt: string;
  approvedAt?: string;
}

// Full match details
export interface MatchDetails extends MatchSummary {
  bracketId: string;
  seasonId: string;
  leagueId: string;
  groupId: string;
  submittedBy: {
    userId: string;
    displayName?: string;
  };
  approvedBy?: {
    userId: string;
    displayName?: string;
  };
  playedAt?: string;
  disputedAt?: string;
  disputeReason?: string;
  notes?: string;
}

// Admin match summary (with extra context)
export interface AdminMatchSummary extends MatchSummary {
  bracketId: string;
  seasonId: string;
  leagueId: string;
  submittedBy?: string;
  approvedBy?: string;
  disputeReason?: string;
}

// User's personal match history item
export interface UserMatchSummary {
  matchId: string;
  bracketId: string;
  seasonId: string;
  leagueId: string;
  groupId: string;
  leagueName?: string;
  seasonName?: string;
  groupName?: string;
  opponent: {
    userId: string;
    displayName: string;
    avatar?: string;
  };
  sets: ScoreSet[];
  won: boolean;
  didUserSubmit: boolean;
  requiresAction: boolean;
  status: MatchStatus;
  submittedAt: string;
  playedAt?: string;
  approvedAt?: string;
  disputedAt?: string;
}

// Submit match request
export interface SubmitMatchRequest {
  player1Id: string;
  player2Id: string;
  sets: ScoreSet[];
  playedAt?: string;
  notes?: string;
}

// Response types
export interface MatchResponse {
  data: MatchSummary;
}

export interface MatchDetailsResponse {
  data: MatchDetails;
}

export interface MatchListResponse {
  data: MatchSummary[];
  meta: {
    total: number;
  };
}

export interface AdminMatchListResponse {
  data: AdminMatchSummary[];
  meta: {
    total: number;
  };
}

export interface UserMatchListResponse {
  data: UserMatchSummary[];
  meta: {
    total: number;
  };
}

export interface ApproveResponse {
  data: {
    matchId: string;
    status: MatchStatus;
    approvedAt: string;
    approvedBy?: string;
  };
}

export interface DisputeResponse {
  data: {
    matchId: string;
    status: MatchStatus;
    disputedAt: string;
    disputeReason?: string;
    disputeDesiredOutcome?: DisputeDesiredOutcome;
    disputeProposedSets?: ScoreSet[];
  };
}

export interface OpponentGamesCount {
  gamesPlayed: number;
  maxGames: number | null;
  remaining: number | null;
}

export const matchService = {
  /**
   * Submit a new match score
   */
  submit: async (
    bracketId: string,
    data: SubmitMatchRequest
  ): Promise<MatchResponse> => {
    return apiClient.authPost<MatchResponse>(`/brackets/${bracketId}/matches`, data);
  },

  /**
   * List matches in a bracket
   */
  listBracketMatches: async (
    bracketId: string,
    options?: { status?: MatchStatus }
  ): Promise<MatchListResponse> => {
    const params = new URLSearchParams();
    if (options?.status) params.set('status', options.status);
    
    const queryString = params.toString();
    const path = `/brackets/${bracketId}/matches`;
    const url = queryString ? `${path}?${queryString}` : path;
    
    return apiClient.authGet<MatchListResponse>(url);
  },

  /**
   * Get a single match
   */
  get: async (bracketId: string, matchId: string): Promise<MatchDetailsResponse> => {
    return apiClient.authGet<MatchDetailsResponse>(`/brackets/${bracketId}/matches/${matchId}`);
  },

  /**
   * Approve a pending match score
   */
  approve: async (bracketId: string, matchId: string): Promise<ApproveResponse> => {
    return apiClient.authPost<ApproveResponse>(`/brackets/${bracketId}/matches/${matchId}/approve`, {});
  },

  /**
   * Dispute a pending match score
   */
  dispute: async (
    bracketId: string,
    matchId: string,
    options?: {
      reason?: string;
      desiredOutcome?: DisputeDesiredOutcome;
      proposedSets?: ScoreSet[];
    }
  ): Promise<DisputeResponse> => {
    return apiClient.authPost<DisputeResponse>(`/brackets/${bracketId}/matches/${matchId}/dispute`, options || {});
  },

  /**
   * Cancel/delete a match
   */
  cancel: async (bracketId: string, matchId: string): Promise<{ data: { deleted: boolean } }> => {
    return apiClient.authDelete(`/brackets/${bracketId}/matches/${matchId}`);
  },

  /**
   * List all matches in a group (for admin)
   */
  listGroupMatches: async (
    groupId: string,
    options?: { status?: MatchStatus; limit?: number }
  ): Promise<AdminMatchListResponse> => {
    const params = new URLSearchParams();
    if (options?.status) params.set('status', options.status);
    if (options?.limit) params.set('limit', options.limit.toString());
    
    const queryString = params.toString();
    const url = queryString ? `/groups/${groupId}/matches?${queryString}` : `/groups/${groupId}/matches`;
    
    return apiClient.authGet<AdminMatchListResponse>(url);
  },

  /**
   * List current user's match history
   */
  listMyMatches: async (
    options?: { status?: MatchStatus; limit?: number }
  ): Promise<UserMatchListResponse> => {
    const params = new URLSearchParams();
    if (options?.status) params.set('status', options.status);
    if (options?.limit) params.set('limit', options.limit.toString());
    
    const queryString = params.toString();
    const url = queryString ? `/me/matches?${queryString}` : '/me/matches';
    
    return apiClient.authGet<UserMatchListResponse>(url);
  },

  /**
   * Get games count against a specific opponent in a bracket
   */
  getOpponentGamesCount: async (
    bracketId: string,
    opponentId: string
  ): Promise<OpponentGamesCount> => {
    const response = await apiClient.authGet<{ data: OpponentGamesCount }>(
      `/brackets/${bracketId}/opponent-games/${opponentId}`
    );
    return response.data;
  },
};

// Helper to format score for display
export function formatScore(sets: ScoreSet[]): string {
  return sets.map(s => `${s.player1Score}-${s.player2Score}`).join(', ');
}

// Helper to determine winner from sets
export function determineWinner(sets: ScoreSet[]): 'player1' | 'player2' | null {
  let player1Sets = 0;
  let player2Sets = 0;
  
  for (const set of sets) {
    if (set.player1Score > set.player2Score) {
      player1Sets++;
    } else if (set.player2Score > set.player1Score) {
      player2Sets++;
    }
  }
  
  if (player1Sets > player2Sets) return 'player1';
  if (player2Sets > player1Sets) return 'player2';
  return null;
}
